import React, { useEffect } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { Card, cx } from '../ui';
import './auth.css';

/** index.css の body padding（固定 Header 分）を、この地の下では外すためのフラグ */
const HTML_CLASS = 'ui-auth-on';

export interface AuthGroundProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * 殻の外の暗い地（画面いっぱい、中身を中央に置く）。ログイン系の画面のほか、
 * ErrorBoundary の代替画面と App の読み込み中表示も同じ地に立つ。
 */
export function AuthGround({ className, children }: AuthGroundProps) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add(HTML_CLASS);
    return () => root.classList.remove(HTML_CLASS);
  }, []);

  return <div className={cx('ui-root ui-auth', className)}>{children}</div>;
}

export interface AuthLayoutProps {
  title: React.ReactNode;
  /** 見出しの下の 1〜2 行の説明 */
  lead?: React.ReactNode;
  /** カード末尾のリンク行（ログインへ戻る など） */
  links?: React.ReactNode;
  children: React.ReactNode;
}

/** ワードマーク + 見出し + 本文 + リンク行を 420px のカードに収める、ログイン系画面の骨格 */
export function AuthLayout({ title, lead, links, children }: AuthLayoutProps) {
  return (
    <AuthGround>
      <Card className="ui-auth__card" padding="lg" data-testid="ui-auth-card">
        <div className="ui-auth__brand">
          <img className="ui-auth__logo" src="/images/title_white.png" alt="送ルート" width={640} height={259} />
        </div>
        <h1 className="ui-auth__title">{title}</h1>
        {lead != null && <p className="ui-auth__lead">{lead}</p>}
        {children}
        {links != null && <div className="ui-auth__links">{links}</div>}
      </Card>
    </AuthGround>
  );
}

export type AuthAlertTone = 'danger' | 'ok';

export interface AuthAlertProps {
  tone?: AuthAlertTone;
  className?: string;
  children: React.ReactNode;
}

/** 送信結果の 1 文。danger は role="alert"、ok は role="status" で読み上げる */
export function AuthAlert({ tone = 'danger', className, children }: AuthAlertProps) {
  const Icon = tone === 'ok' ? CircleCheck : CircleAlert;
  return (
    <div
      className={cx('ui-auth__alert', tone === 'ok' && 'ui-auth__alert--ok', className)}
      role={tone === 'ok' ? 'status' : 'alert'}
    >
      <Icon aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
